import React, { useEffect, useState, useCallback } from 'react';
import { BsChevronLeft, BsChevronRight, BsTools, BsBoxSeam, BsGeoAlt, BsPerson, BsTruckFlatbed } from "react-icons/bs";
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Spinner } from 'reactstrap';
import Session from './auth/Session';
import AuthFunctions from './services/AuthService';

// -- Helpers

// Returns "2026-02-27T00:00:00" style string for the start of a given date.
const toLocalMidnight = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`;
};

// Returns "2026-02-27T23:59:59" style string for the end of a given date.
const toLocalEndOfDay = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T23:59:59`;
};

// Formats a Date as a readable day label: "Wednesday, February 27, 2026"
const formatDayLabel = (date) =>
    date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
});

// Adds or subtracts days from a date, returning a new Date.
const shiftDate = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

// -- DailyScheduleCard
function DailyScheduleCard({ crewName, crewColor, events }) {
    const accentColor = crewColor || "#3788d8";

    // Derive a readable text color for the crew header based on luminance.
    // Simple heuristic: if the hex is "dark", use white text; otherwise dark.
    const isLight = (() => {
        const hex = accentColor.replace("#", "");
        if (hex.length !== 6) return false;
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.55;
    })();

    const headerTextColor = isLight ? "#1a1a1a" : "#ffffff";

    return (
        <div style={{
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
            border: "1px solid #e5e7eb",
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
            height: "100%",
        }}>
            {/* Crew header bar */}
            <div style={{
                backgroundColor: accentColor,
                padding: "0.6rem 1rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
            }}>
                {/* <BsPerson size={16} color={headerTextColor} style={{ flexShrink: 0 }} /> */}
                <span style={{
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    color: headerTextColor,
                    letterSpacing: "0.02em",
                }}>
                    {crewName}
                </span>
            </div>

            {/* Event list */}
            <div style={{ padding: "0.75rem", flex: 1 }}>
                {events.length === 0 ? (
                    <p style={{ fontSize: "0.8rem", color: "#9ca3af", margin: 0, fontStyle: "italic" }}>No jobs scheduled</p>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                        {events.map((event, idx) => (
                            <EventEntry key={event.dbId ?? idx} event={event} isLast={idx === events.length - 1} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// A single job entry within a DailyScheduleCard.
function EventEntry({ event, isLast }) {
    const {
        jobName,
        customerName,
        jobSiteAddress,
        materialRequirements,
        materials,
        equipmentNames,
        start,
        end,
        address,
        crewVehicles,
        crewTrailers
    } = event;

    // Format the time range - e.g. "8:00 AM – 10:00 AM"
    const formatTime = (str) => {
        if (!str) return null;
        const d = new Date(str);
        if (isNaN(d.getTime())) return null;
        return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
    };

    const timeLabel = formatTime(start) && formatTime(end)
    ? `${formatTime(start)} – ${formatTime(end)}`
    : formatTime(start);

    // Show event-level materials if set
    const materialsText = materials?.trim() || null;

    return (
        <div style={{
            paddingBottom: isLast ? 0 : "0.75rem",
            borderBottom: isLast ? "none" : "1px solid #f3f4f6",
        }}>
            {/* Title row: Customer - Job Name */}
            <div style={{ marginBottom: "0.3rem" }}>
                <span style={{ fontWeight: 700, fontSize: "0.875rem", color: "#111827" }}>
                    {customerName ? `${customerName} - ` : ""}
                </span>
                <span style={{ fontWeight: 600, fontSize: "0.875rem", color: "#374151" }}>
                    {jobName || "Untitled Job"}
                </span>
            </div>

            {/* Time */}
            {timeLabel && (
                <div style={{ fontSize: "0.78rem", color: "#6b7280", marginBottom: "0.25rem" }}>
                    {timeLabel}
                </div>
            )}

            {/* Address */}
            {address && (
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                    fontSize: "0.8rem",
                    color: "#4b5563",
                    marginBottom: "0.3rem",
                }}>
                    <BsGeoAlt size={18} style={{ marginTop: "2px", flexShrink: 0, color: "#9ca3af" }} />
                    <a href={`http://maps.google.com/?q=${address}`}>{address}</a>
                </div>
            )}

            {/* Equipment */}
            {equipmentNames?.length > 0 && (
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                    fontSize: "0.8rem",
                    color: "#4b5563",
                    marginBottom: "0.3rem",
                }}>
                    <BsTools size={18} style={{ marginTop: "2px", flexShrink: 0, color: "#9ca3af" }} />
                    <span>{equipmentNames.join(", ")}</span>
                </div>
            )}

            {/* Vehicle / Trailers */}
            {(crewVehicles.length !== 0 || crewTrailers.length !== 0)  && (
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                    fontSize: "0.8rem",
                    color: "#4b5563",
                    marginBottom: "0.3rem",
                }}>
                    <BsTruckFlatbed  size={18} style={{ marginTop: "2px", flexShrink: 0, color: "#9ca3af" }} />
                    {<div>
                        {crewVehicles.join(", ")}
                        {crewTrailers.length !== 0 && (
                            <>
                                <>, {crewTrailers.join(", ")}</>                           
                            </>
                        )}
                    </div>}
                </div>
            )}

            {/* Materials */}
            {materialsText && (
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                    fontSize: "0.8rem",
                    color: "#4b5563",
                }}>
                    <BsBoxSeam size={18} style={{ marginTop: "2px", flexShrink: 0, color: "#9ca3af" }} />
                    <span>{materialsText}</span>
                </div>
            )}
        </div>
    );
}

// -- DailySchedule Page
const DailySchedule = () => {
    const navigate = useNavigate();

    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());

    // Array of { crewId, crewName, crewColor, events[] }
    const [crewSchedules, setCrewSchedules] = useState([]);

    useEffect(() => {
        async function isAuth() {
            const isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) navigate('/login');
        }
        isAuth();
    }, [navigate]);

    useEffect(() => {
        loadDailySchedule(selectedDate);
    }, [selectedDate]);

    // -- Auth headers
    const authHeaders = () => {
        const token = Session.getToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    // -- Data loading
    const getAllCrews = async () => {
        const response = await fetch(`api/Crew/GetAll?activeOnly=true`, {
            headers: authHeaders(),
        });
        if (!response.ok) throw new Error("Failed to load crews.");
        return await response.json(); // [{ id, name, colorHex }]
    };

    const getEventsForCrew = async (crewId, date) => {
        const params = new URLSearchParams({
            crewId,
            start: toLocalMidnight(date),
            end: toLocalEndOfDay(date),
        });
        const response = await fetch(`api/ScheduledEvent/GetByCrewAndDateRange?${params}`, {
            headers: authHeaders(),
        });
        if (!response.ok) throw new Error(`Failed to load events for crew ${crewId}.`);
        return await response.json(); // ScheduledEventDTO[]
    };

    const loadDailySchedule = useCallback(async (date) => {
        setLoading(true);
        setError("");
        try {
            const crews = await getAllCrews();

            // Fan out one request per crew in parallel.
            const results = await Promise.all(
                crews.map(async (crew) => {
                    const dtos = await getEventsForCrew(crew.id, date);
                    return {
                        crewId: crew.id,
                        crewName: crew.name,
                        crewColor: crew.colorHex,
                        events: dtos.map((dto) => ({
                            dbId: dto.id,
                            jobName: dto.jobName,
                            customerName: dto.customerName,
                            jobSiteAddress: dto.jobSiteAddress || "",
                            materialRequirements: dto.materialRequirements || "",
                            materials: dto.materials || "",
                            equipmentNames: dto.equipmentNames || [],
                            start: dto.start,
                            end: dto.end,
                            address: dto.address || "",
                            crewVehicles: crew.vehicleNames,
                            crewTrailers: crew.trailerNames,
                        })),
                    };
                })
            );

            setCrewSchedules(results);
        } catch (e) {
            setError(e.message || "Failed to load daily schedule.");
        } finally {
            setLoading(false);
        }
    }, []);

    // -- Date navigation
    const handlePrev = () => setSelectedDate((d) => shiftDate(d, -1));
    const handleNext = () => setSelectedDate((d) => shiftDate(d, 1));

    const isToday = selectedDate.toDateString() === new Date().toDateString();

    return (
        <>
            {error && (
                <Row>
                    <Col>
                        <div className="alert alert-danger alert-dismissible mb-0" role="alert">
                            {error}
                            <button
                            type="button"
                            className="btn-close"
                            onClick={() => setError("")}
                            aria-label="Close"
                            />
                        </div>
                    </Col>
                </Row>
            )}

            <Container className="container-main">
                {/* Date navigation header */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "1.25rem",
                    margin: "1.25rem 0 1.5rem",
                }}>
                    <Button
                    color="primary"
                    className=""
                    size="md"
                    onClick={handlePrev}
                    >
                        <BsChevronLeft />
                    </Button>

                    <div style={{ textAlign: "center", minWidth: "260px" }}>
                        <h3 style={{margin: 0}}>
                            {formatDayLabel(selectedDate)}
                        </h3>
                    </div>

                    <Button
                    color="primary"
                    className=""
                    size="md"
                    onClick={handleNext}
                    >
                        <BsChevronRight />
                    </Button>
                </div>

                {/* Back to today shortcut - only visible when not on today */}
                {!isToday && (
                    <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
                        <Button
                            color="link"
                            size="sm"
                            onClick={() => setSelectedDate(new Date())}
                            style={{ fontSize: "0.8rem", padding: 0 }}
                        >
                            Back to today
                        </Button>
                    </div>
                )}

                {/* Content */}
                {loading ? (
                    <div style={{ textAlign: "center", marginTop: "3rem" }}>
                        <Spinner />
                    </div>
                ) : (
                    <Row>
                        {crewSchedules.map((crew) => (
                            <Col
                            key={crew.crewId}
                            xs={12}       // 1 column on mobile
                            sm={6}        // 2 columns on tablet
                            lg={4}        // 3 columns on desktop
                            className="mb-4"
                            >
                                <DailyScheduleCard
                                    crewName={crew.crewName}
                                    crewColor={crew.crewColor}
                                    events={crew.events}
                                />
                            </Col>
                        ))}

                        {/* Empty state */}
                        {crewSchedules.length === 0 && !loading && (
                            <Col xs={12}>
                                <div style={{
                                    textAlign: "center",
                                    padding: "3rem 1rem",
                                    color: "#9ca3af",
                                }}>
                                    <p style={{ fontSize: "1rem", marginBottom: "0.25rem" }}>No crews found.</p>
                                </div>
                            </Col>
                        )}
                    </Row>
                )}
            </Container>
        </>
    );
};

export default DailySchedule;